import axios, { AxiosError } from 'axios';
import { createAPIClient } from '@/utils/network-utils/createAPIClient';

import { InvalidOperationError, InvalidStateError } from '@/utils/errors';

import { hasCause, type IErrorDTO } from '@/utils/error-utils';
import { exhaustiveGuard } from '@/utils/function-utils';
import { isNullish, isNullOrEmpty } from '@/utils/null-utils';
import type {
  ISignUpCredentials,
  ILoginCredentials
} from '@/services/models/User/AuthenticatedUser';
import type { IAuthenticatedUserTokens } from '@/services/models/User/IUserTokens';
import { USER_ID_KEY } from '@/configs/env/public';
import { DTO, Nullable } from '@/type-utils';
import Service from '../../Service';
import ServerUserService from '../../serverless/ServerUserService';
import { EnvironmentService } from '../EnvironmentService';
import LoggerService from '../LoggerService';

import type { IAccount } from '../../models/Account';
import {
  AuthenticatedUserModel,
  GuestUserModel,
  IUser,
  UserModel,
  UserType,
  isAuthenticatedUser,
  type IAuthenticatedUser,
  type IGuestUser
} from '../../models/User';

import { FraudAccountEventType } from '../../serverless/ServerFraudService/schemas';
import FraudService from '../FraudService';
import UserServiceMock from './UserServiceMock';
import { InvalidCredentialsError } from './errors/InvalidCredentialsError';
import { UnableToCreateUserAccountError } from './errors/UnableToCreateUserAccountError';
import { UserAccountAlreadyExistsError } from './errors/UserAccountAlreadyExistsError';
import CookieService from '../CookieService';

/** Abstracts operations related to User Authentication. */
export class UserService extends Service {
  private client = createAPIClient({ baseURL: '/api/user' });

  /**
   * Checks if the current user is logged in.
   * @returns `true` if there is currently a logged in user.
   */
  public async isLoggedIn(): Promise<boolean> {
    const currentUser = await this.getCurrentUser().catch(() => null);

    // There is no logged-in user if either there's no current user, or if
    // there is one of `Guest` type.
    const isLoggedIn = !!currentUser && currentUser.type !== UserType.Guest;

    return isLoggedIn;
  }

  /**
   * Tries to retrieve the User ID stored in the current request's cookies.
   *
   * Will return `null` if either the User ID is missing, or if the current
   * environment does not support cookies.
   *
   * @returns The User ID as a string (if found). `null` otherwise.
   */
  public tryGetCurrentUserID(): Nullable<string> {
    return CookieService.tryGet(USER_ID_KEY)?.value ?? null;
  }

  /**
   * Returns a {@link UserModel} representing the user stored in the current session.
   * @param [update=false] - Whether to update the user data from the server. When logged in
   * the account data will be updated from the server and added to the session.
   * @returns A {@link UserModel} representing the current user.
   *
   * @todo Cache on the client. Invalidate when the user logs in and out.
   */
  public async getCurrentUser(update = false): Promise<UserModel> {
    let dtoUser: DTO<IUser>;

    if ((typeof window === "undefined")) {
      dtoUser = update
        ? await ServerUserService.getUpdatedAccount()
        : await ServerUserService.getCurrentUser();
    } else {
      const result = await this.client.get<DTO<IUser>>('', {
        params: { update }
      });
      dtoUser = result.data;
    }

    switch (dtoUser.type) {
      case UserType.Guest:
        return GuestUserModel.from(dtoUser as DTO<IGuestUser>);

      case UserType.Authenticated:
        return AuthenticatedUserModel.from(dtoUser as DTO<IAuthenticatedUser>);

      default:
        return exhaustiveGuard(
          dtoUser.type,
          new InvalidStateError(
            `Retrieved a user DTO from session with an unknown type "${dtoUser.type}".`
          )
        );
    }
  }

  /**
   * Stores the supplied user in {@link SessionService Session}.
   *
   * @param user - User to store in session. Must be a {@link UserModel}.
   * @returns A promise that resolves on success.
   */
  public async saveUserToSession(user: UserModel): Promise<void> {
    if ((typeof window === "undefined")) {
      await ServerUserService.saveCurrentUser(user.toDTO());
      return;
    }

    await this.client.put('', user.toDTO());
  }

  /**
   * Resets the current user to a new guest user.
   * This will update the session data and the user ID cookie.
   */
  public async resetUser(): Promise<void> {
    if ((typeof window === "undefined")) {
      await ServerUserService.resetUser();
      return;
    }

    await this.client.delete('');
  }

  /**
   * Registers a new {@link IAuthenticatedUser authenticated user} and automatically logs in.
   * This does **NOT** automatically merge the user's cart with their existing cart.
   *
   * @param credentials - The new user's credentials.
   *
   * @throws An {@link InvalidOperationError} if this method is called while a user is already logged in.
   * @throws An {@link UserAccountAlreadyExistsError} if the user account already exists and
   * therefor could not be created.
   * @throws An {@link UnableToCreateUserAccountError} if the user account could not be
   * created for any other reason.
   *
   * @returns A {@link UserModel} on success, which will include the user's {@link IAccount account} and a set
   * of {@link IAuthenticatedUserTokens tokens} to authenticate requests.
   */
  public async signUp(
    credentials: ISignUpCredentials
  ): Promise<AuthenticatedUserModel> {
    // First, determine if there is a user currently logged in.
    let currentUser: Nullable<UserModel>;

    try {
      currentUser = await this.getCurrentUser();
    } catch (e) {
      throw new InvalidStateError(
        'Sign Up failed. Could not determine if the session is already logged in as another user.',
        { cause: e as Error }
      );
    }

    // If there is an existing user, and it is not of `Guest` type.
    if (!isNullish(currentUser) && currentUser.type !== UserType.Guest) {
      throw new InvalidOperationError(
        `Already logged in. Please log out first to sign up as another user.`
      );
    }

    try {
      // NEXT api endpoint to create an account.
      await this.client.post<IAccount>('/create-account', credentials);

      // After account creation, authenticate via the DI-selected adapter.
      // Note: signUp intentionally doesn't call logIn() because the new account
      // data may not yet be available in the auth system (async replication).
      // Instead, we call the login endpoint directly which handles this case.
      if ((typeof window === "undefined")) {
        // Server-side: authenticate directly via ServerUserService
        const authResult = await ServerUserService.authenticateUser(
          credentials.email,
          credentials.password
        );

        const userModel = new AuthenticatedUserModel({
          type: UserType.Authenticated,
          uuid: authResult.accountId ?? authResult.account.id,
          email: credentials.email,
          tokens: authResult.tokens as IAuthenticatedUserTokens,
          account: authResult.account as IAccount
        });

        await this.saveUserToSession(userModel);

        // Send signup fraud event (fire and forget)
        FraudService.sendAccountEvent({
          eventType: FraudAccountEventType.Signup,
          account: authResult.account as IAccount
        }).catch((error) => {
          LoggerService.error(error as Error);
        });

        return userModel;
      }

      // Client-side: authenticate via API endpoint
      const loginResponse = await this.client.post<{
        user: DTO<IAuthenticatedUser>;
      }>('/login', {
        email: credentials.email,
        password: credentials.password
      });

      const userModel = AuthenticatedUserModel.from(loginResponse.data.user);

      // Send signup fraud event (fire and forget)
      FraudService.sendAccountEvent({
        eventType: FraudAccountEventType.Signup,
        account: userModel.account
      }).catch((error) => {
        LoggerService.error(error as Error);
      });

      return userModel;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const err = e as AxiosError<Error | IErrorDTO>;
        if (
          !isNullish(err.response?.data) &&
          hasCause(err.response.data, UserAccountAlreadyExistsError.name)
        ) {
          // If the user already exists, throw a more specific error. Please do not ever
          // change this message to include the email address as this would put PII in our
          // logs. Furthermore, make sure this error is never modified to provide any more
          // specific information than the `UnableToCreateUserAccountError` in production
          // environments. This is a security measure to prevent email enumeration attacks.
          throw new UserAccountAlreadyExistsError(
            'Cannot create the user account for the specified email. ' +
              'The email address is already in use.',
            { cause: e as Error }
          );
        }

        throw new UnableToCreateUserAccountError(
          'An unexpected error prevented the user account from being created.',
          { cause: e as Error }
        );
      }

      throw e;
    }
  }

  /**
   * Logs a {@link IUser user} in using the supplied {@link ILoginCredentials credentials}.
   * This does **NOT** automatically merge the user's cart with their existing cart.
   *
   * @param credentials - The credentials to use.
   *
   * @throws An {@link InvalidOperationError} if this method is called while a user is already logged in.
   *
   * @returns A {@link UserModel} on success, which will include the user's {@link IAccount account} and a set
   * of {@link IAuthenticatedUserTokens tokens} to authenticate requests.
   */
  public async logIn(
    credentials: ILoginCredentials
  ): Promise<AuthenticatedUserModel> {
    // First, determine if there is a user currently logged in.
    let currentUser: Nullable<UserModel>;

    try {
      currentUser = await this.getCurrentUser();
    } catch (e) {
      throw new InvalidStateError(
        'Failed to log in. Could not determine if the session is already logged in as another user.',
        { cause: e as Error }
      );
    }

    // If there is an existing user, and it is not of `Guest` type.
    if (!isNullish(currentUser) && currentUser.type !== UserType.Guest) {
      throw new InvalidOperationError(
        `Already logged in. Please log out to log in as another user`
      );
    }

    // No user logged in, or the logged in user is of `Guest` type, continue the login process.

    try {
      if ((typeof window === "undefined")) {
        // Server-side: authenticate directly via ServerUserService
        const authResult = await ServerUserService.authenticateUser(
          credentials.email,
          credentials.password
        );

        const userModel = new AuthenticatedUserModel({
          type: UserType.Authenticated,
          uuid: authResult.accountId ?? authResult.account.id,
          email: credentials.email,
          tokens: authResult.tokens as IAuthenticatedUserTokens,
          account: authResult.account as IAccount
        });

        await this.saveUserToSession(userModel);

        // Send login fraud event (fire and forget)
        FraudService.sendAccountEvent({
          account: authResult.account as IAccount,
          eventType: FraudAccountEventType.Login
        }).catch((error) => {
          LoggerService.error(error as Error);
        });

        return userModel;
      }

      // Client-side: authenticate via API endpoint
      const loginResponse = await this.client.post<{
        user: DTO<IAuthenticatedUser>;
      }>('/login', {
        email: credentials.email,
        password: credentials.password
      });

      // The API endpoint handles saving to session and sending fraud events
      return AuthenticatedUserModel.from(loginResponse.data.user);
    } catch (err) {
      // Transform AxiosError to typed error for client-side calls.
      // Server returns InvalidCredentialsError with mapsToHTTPError, which becomes
      // an HTTP 401 response. We need to detect this and throw a typed error.
      if (axios.isAxiosError(err)) {
        const axiosErr = err as AxiosError<Error | IErrorDTO>;

        // Check if server returned InvalidCredentialsError
        if (
          !isNullish(axiosErr.response?.data) &&
          hasCause(axiosErr.response.data, InvalidCredentialsError.name)
        ) {
          throw new InvalidCredentialsError('Invalid email or password.', {
            cause: err as Error
          });
        }
      }

      // If the login attempt fails for some reason and all other
      // steps send a login failure event via the FraudService.
      FraudService.sendAccountEvent({
        /**
         * @todo Once we can query partial account
         * info by email alone, send info to Forter.
         *
         * **Note:** Fraud account events get validated by the
         * {@link FraudAccountEventSchema}. If the above TODO gets
         * addressed, make sure to update the schema to reflect
         * the way `account` should be sent.
         */
        account: undefined as never,
        eventType: FraudAccountEventType.LoginFailure
      }).catch((error) => {
        LoggerService.error(error as Error);
      });

      throw err;
    }
  }

  /**
   * Given a valid refresh token, get a new set of tokens
   * by refreshing the authentication session.
   *
   * @param refreshToken - A valid refresh token.
   *
   * @returns A new set of {@link IAuthenticatedUserTokens tokens} on success.
   */
  public async refreshTokens(
    refreshToken: string
  ): Promise<IAuthenticatedUserTokens> {
    let result: IAuthenticatedUserTokens;

    if ((typeof window === "undefined")) {
      // Server-side: refresh directly via ServerUserService
      // Cast is safe because refreshTokens only returns authenticated user tokens
      result = (await ServerUserService.refreshTokens(
        refreshToken
      )) as IAuthenticatedUserTokens;
    } else {
      // Client-side: refresh via API endpoint
      const response = await this.client.post<{
        tokens: IAuthenticatedUserTokens;
      }>('/refresh-tokens', { refreshToken });
      result = response.data.tokens;
    }

    const user = await this.getCurrentUser();
    if (isAuthenticatedUser(user)) {
      // Once the user refreshes their session and all other steps
      // completed successfully, send a refresh event via the FraudService.
      FraudService.sendAccountEvent({
        account: user.account,
        eventType: FraudAccountEventType.Refresh
      }).catch((error) => {
        LoggerService.error(error as Error);
      });
    }

    return result;
  }

  /**
   * Given an authenticated user, refresh the user's tokens and save the updated
   * user to session.
   * @param user - The authenticated user to refresh.
   * @returns The updated authenticated user.
   */
  public async refreshUserAuthorization(
    user: IAuthenticatedUser
  ): Promise<IAuthenticatedUser> {
    const { tokens } = user;

    if (isNullOrEmpty(tokens.refreshToken)) {
      throw new InvalidStateError(
        'Cannot refresh user without a refresh token'
      );
    }

    const refreshedTokens = await this.refreshTokens(tokens.refreshToken);

    const refreshedUser = AuthenticatedUserModel.from({
      ...user,
      tokens: refreshedTokens
    });

    await this.saveUserToSession(refreshedUser);

    return refreshedUser;
  }

  /**
   * Confirms a user's email address with a confirmation code.
   * Note: Only applicable for Cognito-based authentication. SLAS does not require confirmation.
   * @param email - The user's email address.
   * @param confirmationCode - The received confirmation code.
   */
  public async confirmUser(
    email: string,
    confirmationCode: string
  ): Promise<void> {
    if ((typeof window === "undefined")) {
      // Server-side: confirm directly via ServerUserService
      await ServerUserService.confirmUser(email, confirmationCode);
    } else {
      // Client-side: confirm via API endpoint
      await this.client.post('/confirm', { email, confirmationCode });
    }

    // The API endpoint handles fraud events, but for server-side direct calls
    // and to ensure consistency, we send here if the user is authenticated
    const user = await this.getCurrentUser();
    if (isAuthenticatedUser(user)) {
      FraudService.sendAccountEvent({
        account: user.account,
        eventType: FraudAccountEventType.EmailConfirmation
      }).catch((error) => {
        LoggerService.error(error as Error);
      });
    }
  }

  /**
   * Resends a confirmation code to a user.
   * Note: Only applicable for Cognito-based authentication. SLAS does not require confirmation.
   * @param email - The user's email address.
   */
  public async resendConfirmationCode(email: string): Promise<void> {
    if ((typeof window === "undefined")) {
      // Server-side: resend directly via ServerUserService
      await ServerUserService.resendConfirmationCode(email);
    } else {
      // Client-side: resend via API endpoint
      await this.client.post('/resend-confirmation', { email });
    }
  }

  /**
   * Initiates a password reset flow and sends a verification code to the
   * user's email address.
   *
   * This method must be called to generate the verification code `resetPassword`
   * requires.
   *
   * @param email - The user's email address.
   */
  public async requestPasswordResetCode(email: string): Promise<void> {
    if ((typeof window === "undefined")) {
      // Server-side: request directly via ServerUserService
      await ServerUserService.requestPasswordReset({ email });
    } else {
      // Client-side: request via API endpoint
      await this.client.post('/password-reset/request', { email });
    }

    const user = await this.getCurrentUser();
    if (isAuthenticatedUser(user)) {
      // Once the user requests a password reset and all other steps completed
      // successfully, send a password reset request event via the FraudService.
      FraudService.sendAccountEvent({
        account: user.account,
        eventType: FraudAccountEventType.PasswordResetRequest
      }).catch((error) => {
        LoggerService.error(error as Error);
      });
    }
  }

  /**
   * Resets a user's password with a verification code. Verification codes can
   * be generated by calling `requestPasswordResetCode`.
   *
   * @param email - The user's email address (required for Cognito, optional for SLAS).
   * @param verificationCode - The verification code (Cognito) or reset token (SLAS).
   * @param newPassword - The new password.
   */
  public async resetPassword(
    email: string,
    verificationCode: string,
    newPassword: string
  ): Promise<void> {
    if ((typeof window === "undefined")) {
      // Server-side: reset directly via ServerUserService
      await ServerUserService.resetPassword({
        email,
        token: verificationCode,
        newPassword
      });
    } else {
      // Client-side: reset via API endpoint
      await this.client.post('/password-reset/confirm', {
        email,
        token: verificationCode,
        newPassword
      });
    }

    const user = await this.getCurrentUser();
    if (isAuthenticatedUser(user)) {
      // Once the user resets their password and all other steps completed
      // successfully, send a password reset event via the FraudService.
      FraudService.sendAccountEvent({
        account: user.account,
        eventType: FraudAccountEventType.PasswordReset
      }).catch((error) => {
        LoggerService.error(error as Error);
      });
    }
  }

  /**
   * Signs a user out and invalidates the active session.
   */
  public async signOut(): Promise<void> {
    try {
      const currentUser = await this.getCurrentUser();

      if (isNullish(currentUser) || currentUser.type === UserType.Guest) {
        throw new InvalidOperationError(
          `Could not sign out; there is no user currently logged in.`
        );
      }

      const isUserAuthenticated = isAuthenticatedUser(currentUser);

      if (!isUserAuthenticated) {
        throw new InvalidOperationError(
          `Could not sign out; the currently logged in user does not support logging out.`
        );
      }

      const { tokens, account } = currentUser;

      if ((typeof window === "undefined")) {
        // Server-side: logout directly via ServerUserService
        await ServerUserService.logout(tokens);
        await this.resetUser();
      } else {
        // Client-side: logout via API endpoint (also resets user)
        await this.client.post('/logout');
      }

      // Once the user logs out and all other steps completed
      // successfully, send a logout event via the FraudService.
      FraudService.sendAccountEvent({
        account,
        eventType: FraudAccountEventType.Logout
      }).catch((err) => {
        LoggerService.error(err as Error);
      });
    } catch (error) {
      // This can happen if the user has logged out in another tab.
      if (error instanceof InvalidOperationError) {
        LoggerService.warn(error);
      } else {
        throw error;
      }
    }
  }
}

export default UserService.withMock(
  new UserServiceMock(UserService)
) as unknown as UserService;
